// MODIFIED: Corrected payload to satisfy C# "required" properties and casing
import { useState, useCallback, useEffect } from "react";
import dtrCorrectionService from "../services/dtrCorrectionService";
import { useGeneralMessage } from "../../common/APICommon";
import { compressImage } from "../../utils/fileUtils";

export const useDTRCorrection = (user) => {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [itineraryList, setItineraryList] = useState([]);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  const [formData, setFormData] = useState({
    employeeId: user.employeePersonalInformationId,
    employeeName: user.employeePersonalInformationFullName,
    date: "",
    timeType: "",
    timeRendered: "",
    timeRenderedNoIn: "",
    timeRenderedNoOut: "",
    reason: "",
    content: null,
    fileName: "",
    fileType: ""
  });

  const fetchData = useCallback(async () => {
    if (!formData.employeeId) return;
    try {
      setLoading(true);
      const [histRes] = await Promise.all([
        dtrCorrectionService.getByEmployee(formData.employeeId)
      ]);
      setRequests(histRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [formData.employeeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (file) => {
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const reader = new FileReader();
      reader.readAsDataURL(compressed);
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          content: reader.result.split(',')[1],
          fileName: file.name,
          fileType: file.type
        }));
      };
    } catch (err) {
      showGeneralMessage("Failed to process file.", false);
    }
  };

  const executeSubmit = async (timeVal, typeVal) => {
    // BACKWARD COMPATIBILITY FIX: 
    // 1. Use camelCase keys (as per legacy JS working state)
    // 2. Ensure ChangeLogRequestId is provided to satisfy C# [Required]
    const payload = {
      employeePersonalInformationId: formData.employeeId,
      approverId: null,
      requestTypeId: 4,
      reason: formData.reason,
      reasonForRejection: null,
      status: "Sent",
      dateRequested: new Date().toISOString(),
      dateCompleted: new Date().toISOString(),
      content: formData.content,
      date: `${formData.date}T00:00:00`,
      timeRendered: `${formData.date}T${timeVal}:00`, // Ensure seconds are included
      timeType: typeVal,
      // CRITICAL FIX: Pass changeLogRequestId: 0 to satisfy the C# Entity requirement
      changeLogItineraries: itineraryList.map(item => ({
        changeLogRequestId: 0,
        timeIn: item.itineraryTimeIn ? `${item.itineraryTimeIn}:00` : null,
        timeOut: item.itineraryTimeOut ? `${item.itineraryTimeOut}:00` : null,
        location: item.itineraryLocation
      }))
    };

    const res = await dtrCorrectionService.create(payload);

    // Create Approval record (Legacy behavior)
    await dtrCorrectionService.createApproval({
      requestId: res.data.id,
      requestTypeId: res.data.requestTypeId,
      primaryApproverId: null,
      primaryApproverStatus: "Pending",
      secondaryApproverStatus: "Pending",
      tertiaryApproverStatus: "Pending",
    });

    return res.data;
  };

  const submitRequest = async () => {
    if (loading) return false;
    setLoading(true);
    try {
      if (formData.timeType === "NoInOut") {
        await executeSubmit(formData.timeRenderedNoIn, "In");
        await executeSubmit(formData.timeRenderedNoOut, "Out");
      } else {
        await executeSubmit(formData.timeRendered, formData.timeType);
      }
      showGeneralMessage("DTR Correction submitted successfully!", true);

      // RESET FORM
      setFormData(prev => ({ ...prev, date: "", timeType: "", timeRendered: "", reason: "", content: null, fileName: "" }));
      setItineraryList([]);
      fetchData();
      return true;
    } catch (err) {
      // Improved error logging
      const errorMsg = err.response?.data?.message || "Check required fields and try again.";
      showGeneralMessage(errorMsg, false);
      console.error("Submission Error Details:", err.response?.data);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading, requests, itineraryList, setItineraryList, formData,
    handleInputChange, handleFileChange, submitRequest,
    showMessage, message, isSuccess
  };
};